import React from 'react';
import {Icons} from '../../assets';
import {Strings} from '../../constants';
import {Container, ContainerSize, ContainerVariant, Spacer, SpacerSize, Stack, StackDirection, StackSpacing} from '../layout';
import {BackgroundPattern} from '../signup-features/background-pattern';
import {Heading, HeadingVariant, HtmlTag, MarginBottom, Text, TextColor, TextVariant} from '../typography';

interface OnboardingFeaturesProps {
  className?: string;
}

export const OnboardingFeatures: React.FC<OnboardingFeaturesProps> = ({className = ''}) => {
  return (
    <div
      className={`relative flex flex-col justify-center items-center p-4 sm:p-6 md:p-8 lg:p-16 min-h-screen overflow-hidden ${className}`.trim()}
      style={{
        background: 'linear-gradient(180deg, #EDE9FB 0%, #F7E8F3 53%, #E5E7FB 100%)',
      }}>
      <BackgroundPattern />

      <Container
        className="relative z-10 w-full max-w-2xl"
        background="bg-transparent"
        size={ContainerSize.MD}
        variant={ContainerVariant.Centered}>
        <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-lg w-full">
          <div className="inline-flex items-center gap-3 px-3 sm:px-4 py-2 bg-gray-100 rounded-full shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
              <Text
                as={HtmlTag.SPAN}
                className="uppercase tracking-wide text-xs sm:text-sm"
                color={TextColor.MUTED}
                variant={TextVariant.BODY_SMALL}>
                {Strings.ONBOARDING_LABEL_FIRST_ROBOTICS}
              </Text>
            </div>
            <div className="w-1 h-1 bg-gray-400 rounded-full" />
            <Text
              as={HtmlTag.SPAN}
              className="uppercase tracking-wide text-xs sm:text-sm"
              color={TextColor.MUTED}
              variant={TextVariant.BODY_SMALL}>
              {Strings.ONBOARDING_LABEL_VISUAL_PORTFOLIOS}
            </Text>
            <div className="w-1 h-1 bg-gray-400 rounded-full" />
            <Text
              as={HtmlTag.SPAN}
              className="uppercase tracking-wide text-xs sm:text-sm"
              color={TextColor.MUTED}
              variant={TextVariant.BODY_SMALL}>
              {Strings.ONBOARDING_LABEL_INTERNSHIPS}
            </Text>
          </div>

          <Spacer size={SpacerSize.LG} />

          <Heading className="text-gray-900 break-words" level={1} marginBottom={MarginBottom.NONE} variant={HeadingVariant.PAGE_TITLE}>
            {Strings.ONBOARDING_HEADLINE_PART1}{' '}
            <span className="bg-gradient-to-r from-blue-800 to-blue-400 bg-clip-text text-transparent font-bold">
              {Strings.ONBOARDING_HEADLINE_ROBOTICS}
            </span>{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-lime-400 bg-clip-text text-transparent font-bold">
              {Strings.ONBOARDING_HEADLINE_GRIND}
            </span>{' '}
            {Strings.ONBOARDING_HEADLINE_PART2}
          </Heading>

          <Spacer size={SpacerSize.LG} />

          <Text color={TextColor.SECONDARY} variant={TextVariant.BODY}>
            {Strings.ONBOARDING_PARAGRAPH_ONE}
          </Text>

          <Spacer size={SpacerSize.LG} />

          <Text color={TextColor.SECONDARY} variant={TextVariant.BODY}>
            {Strings.ONBOARDING_PARAGRAPH_TWO}
          </Text>

          <Spacer size={SpacerSize.LG} />

          <Stack direction={StackDirection.Vertical} spacing={StackSpacing.MD}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Text as={HtmlTag.SPAN} className="font-bold text-blue-500 text-sm" variant={TextVariant.BODY_SMALL}>
                  {Strings.ONBOARDING_CREDS_LABEL.charAt(0)}
                </Text>
              </div>
              <Text className="flex-1" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
                <strong>{Strings.ONBOARDING_CREDS_LABEL}</strong>: {Strings.ONBOARDING_CREDS_DESCRIPTION}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Text as={HtmlTag.SPAN} className="font-bold text-blue-500 text-sm" variant={TextVariant.BODY_SMALL}>
                  {Strings.ONBOARDING_RECCOS_LABEL.charAt(0)}
                </Text>
              </div>
              <Text className="flex-1" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
                <strong>{Strings.ONBOARDING_RECCOS_LABEL}</strong>: {Strings.ONBOARDING_RECCOS_DESCRIPTION}
              </Text>
            </div>
          </Stack>

          <Spacer size={SpacerSize.LG} />

          <Text className="uppercase tracking-wide" color={TextColor.MUTED} variant={TextVariant.BODY_SMALL}>
            {Strings.ONBOARDING_SECTION_WHY_IT_MATTERS}
          </Text>

          <Spacer size={SpacerSize.MD} />

          <Stack direction={StackDirection.Vertical} spacing={StackSpacing.MD}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text className="flex-1" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
                {Strings.ONBOARDING_CHECKPOINT_ONE}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text className="flex-1" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
                {Strings.ONBOARDING_CHECKPOINT_TWO}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text className="flex-1" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
                {Strings.ONBOARDING_CHECKPOINT_THREE}
              </Text>
            </div>
          </Stack>
          <Spacer size={SpacerSize.LG} />

          <Text className="font-medium" color={TextColor.SECONDARY} variant={TextVariant.BODY}>
            {Strings.ONBOARDING_FINAL_SENTENCE}
          </Text>
        </div>
      </Container>
    </div>
  );
};
